import React, { useEffect, useState } from 'react';
import { billingService } from '../../api/services/billing.service';
import { BillingType, CreditAccountDTO, formatMoney } from '../../types/billing';
import { istMidnightToday, istToServerUtc } from './closeBooksRules';

interface Props {
  /** Preselects this organisation (opened from its credit account). */
  organizationId?: string;
  onClose: () => void;
  /** `created` is false when there was nothing to invoice. */
  onDone: (message: string, created: boolean) => void;
}

const inputCls =
  'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:ring-indigo-500 focus:border-indigo-500';

/**
 * Step 1 of closing a postpaid period: a DRAFT invoice of every usage charge before
 * the cut-off that is on no invoice yet. Safe to repeat — each charge is invoiced once,
 * so a second run with the same cut-off finds nothing.
 */
const InvoiceUsageModal: React.FC<Props> = ({ organizationId, onClose, onDone }) => {
  const [accounts, setAccounts] = useState<CreditAccountDTO[]>([]);
  const [orgId, setOrgId] = useState(organizationId || '');
  const [cutoff, setCutoff] = useState(istMidnightToday());
  const [taxPercent, setTaxPercent] = useState('18');
  const [dueInDays, setDueInDays] = useState('15');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    billingService.getCreditAccounts({ page: 1, size: 200 }).then((r) => {
      // Usage invoices are refused for prepaid accounts: the top-up already paid.
      if (r.success && r.data) setAccounts(r.data.content.filter((a) => a.billingType === BillingType.POSTPAID));
    });
  }, []);

  const before = istToServerUtc(cutoff);
  const account = accounts.find((a) => a.organizationId === orgId);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orgId || !before) return;
    setBusy(true);
    setError(null);
    const r = await billingService.invoiceUsage(orgId, before, {
      taxPercent: taxPercent === '' ? undefined : Number(taxPercent),
      dueInDays: dueInDays === '' ? undefined : Number(dueInDays),
    });
    setBusy(false);
    if (!r.success) {
      setError(r.error || 'Failed to invoice usage');
      return;
    }
    const name = account?.organizationName || orgId;
    onDone(
      r.data
        ? `Draft ${r.data.invoiceNumber} for ${name}: ${formatMoney(r.data.totalAmount, r.data.currency)} incl. GST. Check it against the voice statement, then finalize.`
        : `Nothing to invoice for ${name} before ${cutoff.replace('T', ' ')} IST.`,
      !!r.data,
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto mx-4">
        <h2 className="text-xl font-semibold mb-1 dark:text-white">Invoice usage</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          A DRAFT of every usage charge before the cut-off that is not on an invoice yet, one line per
          business, service and carrier. Paying it in full settles the postpaid balance.
        </p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Postpaid account *</label>
            <select required value={orgId} onChange={(e) => setOrgId(e.target.value)} className={inputCls}>
              <option value="">Select…</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.organizationId}>
                  {a.organizationName || a.organizationId} · {formatMoney(a.creditBalance, a.currency)}
                </option>
              ))}
              {orgId && !account && <option value={orgId}>{orgId}</option>}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Cut-off (IST) *</label>
            <input type="datetime-local" required value={cutoff} onChange={(e) => setCutoff(e.target.value)} className={inputCls} />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Charges strictly before this instant.{before ? ` Sent as ${before.replace('T', ' ')} UTC.` : ''}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">GST %</label>
              <input type="number" step="0.01" min="0" value={taxPercent} onChange={(e) => setTaxPercent(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Due in (days)</label>
              <input type="number" min="0" value={dueInDays} onChange={(e) => setDueInDays(e.target.value)} className={inputCls} />
            </div>
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button type="submit" disabled={busy || !orgId || !before} className="tl-btn tl-btn-primary disabled:opacity-50">
              {busy ? 'Invoicing…' : 'Create draft'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InvoiceUsageModal;
